import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type Pool = components['schemas']['PoolOut']
export type PoolMember = components['schemas']['PoolMemberOut']
export type PoolStockout = components['schemas']['PoolStockoutOut']
export type PoolCreate = components['schemas']['PoolCreateIn']
export type LinkPreview = components['schemas']['LinkPreviewOut']
export type DeletePreview = components['schemas']['DeletePreviewOut']

/** Ligar, desligar e apagar pool mexem no scheduler, nos canais e nos vídeos. */
function invalidateAround(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.pools.all })
  void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
  void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
}

export function usePools() {
  return useQuery({
    queryKey: queryKeys.pools.list(),
    queryFn: async (): Promise<Pool[]> => {
      const { data } = await api.GET('/api/v1/pools')
      if (!data) throw new Error('pools response empty')
      return data.data.pools
    },
    staleTime: 30_000,
  })
}

export function usePool(slug: string | null) {
  return useQuery({
    queryKey: queryKeys.pools.byId(slug ?? ''),
    queryFn: async (): Promise<Pool> => {
      const { data } = await api.GET('/api/v1/pools/{slug}', { params: { path: { slug: slug ?? '' } } })
      if (!data) throw new Error('pool response empty')
      return data.data
    },
    enabled: Boolean(slug),
    staleTime: 15_000,
  })
}

export function useCreatePool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: PoolCreate): Promise<Pool> => {
      const { data } = await api.POST('/api/v1/pools', { body })
      if (!data) throw new Error('create pool empty')
      return data.data
    },
    onSuccess: () => {
      invalidateAround(qc)
    },
  })
}

export function useRenamePool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { slug: string; name: string }): Promise<Pool> => {
      const { data } = await api.PATCH('/api/v1/pools/{slug}', {
        params: { path: { slug: input.slug } },
        body: { name: input.name },
      })
      if (!data) throw new Error('rename pool empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pools.all })
    },
  })
}

/** Os números do diálogo de ligar um canal ao pool, sem mudar nada. */
export function useLinkPreview(slug: string | null, channel: string | null) {
  return useQuery({
    queryKey: queryKeys.pools.linkPreview(slug ?? '', channel ?? ''),
    queryFn: async (): Promise<LinkPreview> => {
      const { data } = await api.GET('/api/v1/pools/{slug}/link-preview', {
        params: { path: { slug: slug ?? '' }, query: { channel: channel ?? '' } },
      })
      if (!data) throw new Error('link preview empty')
      return data.data
    },
    enabled: Boolean(slug) && Boolean(channel),
    staleTime: 0,
  })
}

export function useLinkChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { slug: string; channel: string }): Promise<LinkPreview> => {
      const { data } = await api.POST('/api/v1/pools/{slug}/members', {
        params: { path: { slug: input.slug } },
        body: { channel: input.channel },
      })
      if (!data) throw new Error('link channel empty')
      return data.data
    },
    onSuccess: () => {
      invalidateAround(qc)
    },
  })
}

export function useUnlinkChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { slug: string; channel: string }): Promise<void> => {
      await api.DELETE('/api/v1/pools/{slug}/members/{channel}', {
        params: { path: { slug: input.slug, channel: input.channel } },
      })
    },
    onSuccess: () => {
      invalidateAround(qc)
    },
  })
}

export function useDeletePreview(slug: string | null) {
  return useQuery({
    queryKey: queryKeys.pools.deletePreview(slug ?? ''),
    queryFn: async (): Promise<DeletePreview> => {
      const { data } = await api.GET('/api/v1/pools/{slug}/delete-preview', {
        params: { path: { slug: slug ?? '' } },
      })
      if (!data) throw new Error('delete preview empty')
      return data.data
    },
    enabled: Boolean(slug),
    staleTime: 0,
  })
}

export function useDeletePool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (slug: string): Promise<void> => {
      await api.DELETE('/api/v1/pools/{slug}', {
        params: { path: { slug }, query: { confirm: slug } },
      })
    },
    onSuccess: () => {
      invalidateAround(qc)
    },
  })
}
